/**
 * Mockups — `modules/mockups/routes.ts` `clientMockupsRoutes` (session 48).
 *
 * The list (and the nav badge's count, which is the same read), one mockup with every
 * version and comment, a comment or a reply, a thread resolved or reopened (session 55), and
 * the answer on a version. Neither write is money:
 * approving a design is the client's sign-off on a look, not a commitment, so there is no
 * `Idempotency-Key` and a double-clicked *Approve* is an honest `Conflict`.
 */

import type {
  MockupCommentInput,
  MockupGuestCommentInput,
  MockupReviewInput,
} from '@fareast/shared'

import { useApiMutation, useApiQuery } from '../hooks.js'
import { queryKeys } from '../keys.js'

/** `GET /api/clients/me/mockups`. */
export function useMockupsPage(
  options: { readonly enabled?: boolean; readonly inline?: boolean } = {},
) {
  return useApiQuery({
    key: queryKeys.mockups.page(),
    request: (api) => api.rest.clients.me.mockups.$get(),
    ...(options.enabled === undefined ? {} : { enabled: options.enabled }),
    ...(options.inline === undefined ? {} : { inline: options.inline }),
  })
}

/** `GET /api/clients/me/mockups/:ref`. */
export function useMockup(ref: string) {
  return useApiQuery({
    key: queryKeys.mockups.byRef(ref),
    request: (api) => api.rest.clients.me.mockups[':ref'].$get({ param: { ref } }),
  })
}

/** `POST /api/clients/me/mockups/:ref/comments`. Answers the mockup, comment and all. */
export function useCommentOnMockup() {
  return useApiMutation({
    module: 'mockups',
    // A client's comment raises the studio's "Read the comments" task, which the client's own
    // board shows.
    alsoInvalidates: ['tasks', 'core'],
    request: (api, variables: { ref: string } & MockupCommentInput) => {
      const { ref, ...json } = variables
      return api.rest.clients.me.mockups[':ref'].comments.$post({ param: { ref }, json })
    },
  })
}

/**
 * `POST /api/clients/me/mockups/:ref/comments/:id/resolve` and `…/reopen` — a thread decided by a
 * member (session 55). The comment must be the mockup's; another's id is the API's 404.
 */
export function useDecideMockupThread() {
  return useApiMutation({
    module: 'mockups',
    request: (api, variables: { ref: string; id: string; decision: 'resolve' | 'reopen' }) => {
      const param = { ref: variables.ref, id: variables.id }
      const thread = api.rest.clients.me.mockups[':ref'].comments[':id']
      return variables.decision === 'resolve'
        ? thread.resolve.$post({ param })
        : thread.reopen.$post({ param })
    },
  })
}

/**
 * `GET /api/mockups/link/:publicId` — what the link's viewer shows (session 49). Needs no
 * session; one, when there is one, decides who the viewer says is looking. A dead link is the
 * page's to say, so its `404` is kept here and is final.
 */
export function useMockupLink(publicId: string) {
  return useApiQuery({
    key: queryKeys.mockups.link(publicId),
    request: (api) => api.rest.mockups.link[':publicId'].$get({ param: { publicId } }),
    inline: true,
    refusalIsFinal: true,
  })
}

/** `POST /api/mockups/link/:publicId/comments` — somebody with the link and no account. */
export function useGuestComment() {
  return useApiMutation({
    module: 'mockups',
    request: (api, variables: { publicId: string } & MockupGuestCommentInput) => {
      const { publicId, ...json } = variables
      return api.rest.mockups.link[':publicId'].comments.$post({ param: { publicId }, json })
    },
  })
}

/** `POST /api/clients/me/mockups/:ref/review`. */
export function useReviewMockup() {
  return useApiMutation({
    module: 'mockups',
    alsoInvalidates: ['tasks', 'core'],
    request: (api, variables: { ref: string } & MockupReviewInput) => {
      const { ref, ...json } = variables
      return api.rest.clients.me.mockups[':ref'].review.$post({ param: { ref }, json })
    },
  })
}
